import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useScrollToSection } from "../../app/useScrollToSection";
import { getParamAsInt } from "../../app/utils";
import StepProgressBar, { StepStatus } from "../../components/StepProgressBar";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import { SelectDevelopmentView } from "../../components/input/SelectDevelopmentView";
import RtkqResultViewer from "../../components/RtkqResultViewer";
import { TeacherWritingSubTabs } from "../writing/teacher/TeacherWriting";
import {
	StimulusMaterialSaveModel,
	useAddEditStimulusMaterialMutation,
	StimulusMaterialEditModel,
} from "../stimulusMaterial/stimulusMaterialApiSlice";
import {
	AdvancedSettingsViewModel,
	PeerReviewSettingsViewModel,
	SavePromptViewModel,
	GetPromptViewModel,
	useLazyAddPromptDefaultsQuery,
	useAddPromptMutation,
	useLazyEditPromptInfoQuery,
	useEditPromptMutation,
	EmailPromptViewModel,
	useSendNotifyEmailMutation,
	useLazyEmailListQuery,
	EmailPromptListViewModel,
} from "./promptsApiSlice";
import PromptStep from "./PromptStep";
import AssignmentStep from "./AssignmentStep";
import OrganizerStep from "./OrganizerStep";
import OptionsStep from "./OptionsStep";
import PeerReviewStep from "./PeerReviewStep";
import SharePromptStep from "./SharePromptStep";

export enum PromptSteps {
	Prompt = 1,
	Assignment,
	Organizer,
	PeerReviews,
	Options,
	Share,
}

const initialSteps = [
	{ id: PromptSteps.Prompt, name: "Prompt", status: StepStatus.Current },
	{ id: PromptSteps.Assignment, name: "Assignment", status: StepStatus.Upcoming },
	{ id: PromptSteps.Organizer, name: "Organizer", status: StepStatus.Upcoming },
	{ id: PromptSteps.PeerReviews, name: "Peer Reviews", status: StepStatus.Upcoming },
	{ id: PromptSteps.Options, name: "Options", status: StepStatus.Upcoming },
	{ id: PromptSteps.Share, name: "Share Prompt", status: StepStatus.Upcoming },
];

export default function AddEditPrompt() {
	const [queryParams] = useSearchParams();
	const promptId = getParamAsInt(queryParams, "promptId");
	useScrollToSection();

	const [view, setView] = useState<string>("react");

	const [step, setStep] = useState(PromptSteps.Prompt);
	const [steps, setSteps] = useState(initialSteps);
	const [disableSave, setDisableSave] = useState(true);
	const [promptStepHasError, setPromptStepHasError] = useState(true);
	const [assignmentStepHasError, setAssignmentStepHasError] = useState(false);
	const [optionsStepHasError, setOptionsStepHasError] = useState(false);
	const [model, setModel] = useState<SavePromptViewModel>();
	const [advancedSettingsModel, setAdvancedSettingsModel] = useState<AdvancedSettingsViewModel>();
	const [peerReviewSettingsModel, setPeerReviewSettingsModel] =
		useState<PeerReviewSettingsViewModel>();
	const [emailListModel, setEmailListModel] = useState<EmailPromptViewModel>();
	const [saveError, setSaveError] = useState("");
	const [newPromptStimulusMaterials, setNewPromptStimulusMaterials] = useState<
		StimulusMaterialSaveModel[]
	>([]);

	const [defaults, setDefaults] = useState<GetPromptViewModel>();

	const [getNewPromptDefaults, getNewPromptDefaultsResult] = useLazyAddPromptDefaultsQuery();
	const [getEditPromptDefaults, getEditPromptDefaultsResult] = useLazyEditPromptInfoQuery();
	const [getEmailList, getEmailListResult] = useLazyEmailListQuery();

	const [addPrompt] = useAddPromptMutation();
	const [addEditStimMaterial] = useAddEditStimulusMaterialMutation();
	const [editPrompt] = useEditPromptMutation();
	const [emailTeachers] = useSendNotifyEmailMutation();

	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		if (location.state != null) {
			if (location.state.step) {
				setStep(location.state.step);
				if (promptId) setPromptStepHasError(false);
			}
		}
		navigate(location, { replace: true });
	}, []);

	useEffect(() => {
		if (promptId) {
			getEditPromptDefaults(promptId)
				.unwrap()
				.then((result) => {
					setDefaults(result);
				});
		} else {
			getNewPromptDefaults()
				.unwrap()
				.then((result) => {
					setDefaults(result);
				});
		}
	}, []);

	function updateSteps() {
		setSteps(
			steps.map((s) =>
				s.id === step
					? {
							...s,
							status:
								(s.id === PromptSteps.Prompt && promptStepHasError) ||
								(s.id === PromptSteps.Assignment && assignmentStepHasError) ||
								(s.id === PromptSteps.Options && optionsStepHasError)
									? StepStatus.Error
									: s.status == StepStatus.Current ||
										  s.status == StepStatus.Complete
										? StepStatus.Complete
										: StepStatus.Current,
						}
					: {
							...s,
							status:
								(s.id === PromptSteps.Prompt && promptStepHasError) ||
								(s.id === PromptSteps.Assignment && assignmentStepHasError) ||
								(s.id === PromptSteps.Options && optionsStepHasError)
									? StepStatus.Error
									: s.status == StepStatus.Upcoming
										? StepStatus.Upcoming
										: StepStatus.Complete,
						}
			)
		);
	}

	useEffect(() => {
		updateSteps();
		const newModel = updateModel(false);
		setModel(newModel);
	}, [step]);

	useEffect(() => {
		if (!promptStepHasError && !assignmentStepHasError && !optionsStepHasError) {
			setDisableSave(false);
		} else {
			setDisableSave(true);
		}
		updateSteps();
	}, [promptStepHasError, assignmentStepHasError, optionsStepHasError]);

	useEffect(() => {
		setModel(createSaveModel());
		setAdvancedSettingsModel(createAdvancedSettingsModel());
		setPeerReviewSettingsModel(defaults?.peerReviewSettings);

		if (defaults && !defaults.wasAlreadyShared) {
			var emailModel: EmailPromptListViewModel = {
				promptIds: [],
				includesDisabledPrompts: false,
				courseId:
					Number(
						defaults.promptEnabledInCoursesList.find((c) => c.isCreatedInCourse)?.value
					) ?? null,
			};
			getEmailList(emailModel)
				.unwrap()
				.then((model) => {
					setEmailListModel(model);
				});
		}
	}, [defaults]);

	function updateModel(isSaving: boolean): SavePromptViewModel | undefined {
		if (!model) return undefined;

		if (isSaving && advancedSettingsModel && defaults) {
			const resetAdvancedSettingsModel: AdvancedSettingsViewModel = {
				...advancedSettingsModel,
				timeAllowed: advancedSettingsModel.timerEnabled
					? advancedSettingsModel.timeAllowed
					: defaults.advancedSettings.timeAllowed,
				retriesAllowed: advancedSettingsModel.retriesEnabled
					? advancedSettingsModel.retriesAllowed
					: defaults.advancedSettings.retriesAllowed,
				finalizeDraftNumber: advancedSettingsModel.finalizeEnabled
					? advancedSettingsModel.finalizeDraftNumber
					: defaults.advancedSettings.finalizeDraftNumber,
				dueDate: advancedSettingsModel.dueDateEnabled
					? advancedSettingsModel.dueDate
					: defaults.advancedSettings.dueDate,
			};

			const editModel: SavePromptViewModel = {
				...model,
				advancedSettings: resetAdvancedSettingsModel,
				peerReviewSettings: peerReviewSettingsModel,
			};
			return editModel;
		} else {
			const editModel: SavePromptViewModel = {
				...model,
				advancedSettings: advancedSettingsModel,
				peerReviewSettings: peerReviewSettingsModel,
			};
			return editModel;
		}
	}

	function createSaveModel(): SavePromptViewModel | undefined {
		if (!defaults) return undefined;
		const editModel: SavePromptViewModel = {
			isNew: defaults.isNew,
			canEdit: defaults.canEdit,
			enabled: defaults.enabled,
			statusDate: defaults.statusDate,
			name: defaults.name,
			text: defaults.text,
			genreId: defaults.genreId,
			contentAreaIds: defaults.contentAreaIds,
			contentAreas: defaults.contentAreas,
			advancedSettings: createAdvancedSettingsModel(),
			peerReviewSettings: defaults.peerReviewSettings,
			selectedPromptEnabledInCoursesList: defaults.promptEnabledInCoursesList
				.filter((c) => c.selected)
				.map((c) => c.value),
			isShared: defaults.isShared,
			wasAlreadyShared: defaults.wasAlreadyShared,
		};
		return editModel;
	}

	function createAdvancedSettingsModel(): AdvancedSettingsViewModel | undefined {
		if (!defaults) return undefined;
		const advancedSettings = defaults.advancedSettings;
		const advancedModel: AdvancedSettingsViewModel = {
			assignedToIds: advancedSettings.assignedToIds,
			dueDateEnabled: advancedSettings.dueDateEnabled,
			dueDate: advancedSettings.dueDate,
			dueDateRequired: advancedSettings.dueDateRequired,
			selectedReminders: advancedSettings.selectedReminders,
			timerEnabled: advancedSettings.timerEnabled,
			timeAllowed: advancedSettings.timeAllowed,
			retriesEnabled: advancedSettings.retriesEnabled,
			retriesAllowed: advancedSettings.retriesAllowed,
			titleRequired: advancedSettings.titleRequired,
			requireSources: advancedSettings.requireSources,
			finalizeEnabled: advancedSettings.finalizeEnabled,
			finalizeDraftNumber: advancedSettings.finalizeDraftNumber,
			scoreDraftNumber: advancedSettings.scoreDraftNumber,
			grammarCheckDraftThreshold: advancedSettings.grammarCheckDraftThreshold,
			specializedVocabularyList: advancedSettings.specializedVocabularyList,
			allowCopyPaste: advancedSettings.allowCopyPaste,
			isOrganizerRequired: advancedSettings.isOrganizerRequired,
			selectedOrganizerId: advancedSettings.selectedOrganizerId,
			canSetPlagiarismSettings: advancedSettings.canSetPlagiarismSettings,
			plagiarismCheckerEnabled: advancedSettings.plagiarismCheckerEnabled,
			plagiarismCheckerAllDrafts: advancedSettings.plagiarismCheckerAllDrafts,
			plagiarismCheckedDrafts: advancedSettings.plagiarismCheckedDrafts,
		};
		return advancedModel;
	}

	function handleNextStep() {
		if (step === PromptSteps.Share) return;
		else {
			setStep(step + 1);
		}
	}

	function handlePreviousStep() {
		if (step === PromptSteps.Prompt) return;
		else {
			setStep(step - 1);
		}
	}

	function updateNewPromptStimMaterials(stimMaterials: StimulusMaterialSaveModel[]) {
		setNewPromptStimulusMaterials(stimMaterials);
	}

	function updateStimMaterialsWithPromptId(newPromptId: number): StimulusMaterialSaveModel[] {
		const updatedStimMaterials = newPromptStimulusMaterials.map((sm) => {
			const updatedEditModel: StimulusMaterialEditModel = {
				...sm.editModel,
				promptId: newPromptId,
			};
			const updatedModel: StimulusMaterialSaveModel = { ...sm, editModel: updatedEditModel };
			return updatedModel;
		});
		return updatedStimMaterials;
	}

	function handleSave() {
		setDisableSave(true);
		const temp = updateModel(true);

		if (temp) {
			if (promptId) {
				editPrompt({ editPromptId: promptId, model: temp })
					.unwrap()
					.then(() => {
						notifyTeachers(promptId, temp);
						navigateToPromptLibrary();
					})
					.catch((error) => {
						handleSaveError(error, temp);
					});
			} else {
				addPrompt(temp)
					.unwrap()
					.then((newPromptId) => {
						notifyTeachers(newPromptId, temp);
						if (newPromptStimulusMaterials.length > 0) {
							AddStimulusMaterial(newPromptId, temp.name);
						} else {
							navigateToPromptLibrary();
						}
					})
					.catch((error) => {
						handleSaveError(error, temp);
					});
			}
		}
	}

	async function AddStimulusMaterial(newPromptId: number, promptName: string) {
		let hasError = false;
		let errorMessage = `The following stimulus material for your prompt, ${promptName}, could not be saved: `;
		const stimulusMaterials = updateStimMaterialsWithPromptId(newPromptId);

		const promises = stimulusMaterials.map(async (sm) => {
			await addEditStimMaterial(sm)
				.unwrap()
				.catch((error) => {
					hasError = true;
					errorMessage += `\n- ${sm.editModel.label}: ${error.data}`;
				});
		});
		await Promise.all(promises);

		if (!hasError) {
			navigateToPromptLibrary();
		} else {
			navigate("/writing", {
				state: {
					errorMessage: errorMessage,
					promptName: promptName,
					promptId: newPromptId,
					subTab: TeacherWritingSubTabs.PromptLibrary,
				},
			});
		}
	}

	function notifyTeachers(promptId: number, promptModel: SavePromptViewModel) {
		if (
			emailListModel &&
			emailListModel.proctorIds?.length > 0 &&
			promptModel.isShared &&
			!promptModel.wasAlreadyShared
		) {
			const model: EmailPromptViewModel = {
				...emailListModel,
				promptIds: [promptId],
				includesDisabledPrompts: !promptModel.enabled,
			};
			emailTeachers(model)
				.unwrap()
				.catch((error) => {
					console.log(error);
				});
		}
	}

	function handleSaveError(error: any, temp: SavePromptViewModel) {
		setSaveError(error.data);
		setStep(PromptSteps.Prompt);
		setModel(temp);
		setAdvancedSettingsModel(temp.advancedSettings);
		setPeerReviewSettingsModel(temp.peerReviewSettings);
		scrollTo(0, 0);
		setDisableSave(false);
	}

	function navigateToPromptLibrary() {
		navigate("/writing", {
			state: {
				subTab: TeacherWritingSubTabs.PromptLibrary,
			},
		});
	}

	return (
		<>
			<section>
				<div className="flex justify-between">
					<div className="w-3/4">
						<h1>{promptId ? "Edit a Prompt" : "Create a Prompt"}</h1>
					</div>
					<SelectDevelopmentView
						value={view}
						setValue={(v) => setView(v as string)}
						simpleListBoxId="viewFilter"
						simpleListBoxClasses="print:hidden"
					/>
				</div>
				<p className="mt-4">
					Fill in the required fields and click <b>Save Prompt</b> to save the prompt. To
					navigate through all the steps and options available for prompts, click{" "}
					<b>Next Step</b> or <b>Previous Step</b>. You can also click on the steps in the
					progress bar to jump to any step. You can only save the prompt once you complete
					the information in Step 1.
				</p>
			</section>
			{view === "react" && (
				<section>
					<StepProgressBar
						activeStep={steps.find((s) => s.id === step)}
						steps={steps}
						setStep={setStep}
						useLargeMediaBreakpoint={true}
					/>
					{saveError && (
						<AlertMessage
							className="mt-4 text-sm"
							type={AlertLevel.Error}
							message={saveError}
							showIcon={true}
						/>
					)}
					{defaults && model && advancedSettingsModel && peerReviewSettingsModel && (
						<div className="mt-6">
							{step === PromptSteps.Prompt && (
								<PromptStep
									model={model}
									promptId={promptId}
									genres={defaults.essayCategories}
									availableContentAreas={defaults.availableContentAreas}
									setModel={setModel}
									setHasError={setPromptStepHasError}
									updateNewPromptStimMaterialsModel={updateNewPromptStimMaterials}
								/>
							)}
							{step === PromptSteps.Assignment && (
								<AssignmentStep
									model={advancedSettingsModel}
									availableUsers={defaults.advancedSettings.availableUsers}
									availableReminders={
										defaults.advancedSettings.availableReminders
									}
									setAdvancedModel={setAdvancedSettingsModel}
									setHasError={setAssignmentStepHasError}
								/>
							)}
							{step === PromptSteps.Organizer && (
								<OrganizerStep
									model={advancedSettingsModel}
									organizersByGenre={defaults.advancedSettings.organizersByGenre}
									genres={defaults.essayCategories}
									genreId={model.genreId}
									setAdvancedModel={setAdvancedSettingsModel}
								/>
							)}
							{step === PromptSteps.PeerReviews && (
								<PeerReviewStep
									promptName={model.name}
									model={peerReviewSettingsModel}
									genreId={model.genreId}
									setPeerReviewModel={setPeerReviewSettingsModel}
								/>
							)}
							{step === PromptSteps.Options && (
								<OptionsStep
									model={advancedSettingsModel}
									setAdvancedModel={setAdvancedSettingsModel}
									setHasError={setOptionsStepHasError}
								/>
							)}
							{step === PromptSteps.Share && (
								<SharePromptStep
									model={model}
									emailListModel={emailListModel}
									promptEnabledInCoursesList={defaults.promptEnabledInCoursesList}
									setModel={setModel}
									setEmailListModel={setEmailListModel}
								/>
							)}
						</div>
					)}
					<div className="flex justify-between mt-3">
						<div>
							<button
								id="cancel-button"
								className="btn btn-neutral mr-2"
								onClick={() => navigateToPromptLibrary()}>
								Cancel
							</button>
							{step != PromptSteps.Prompt && (
								<button
									id="previous-step-button"
									className="btn btn-neutral mr-2"
									onClick={handlePreviousStep}>
									Previous Step
								</button>
							)}
						</div>
						<div>
							{step != PromptSteps.Share && (
								<button
									id="next-step-button"
									className="btn btn-neutral mr-2"
									onClick={handleNextStep}>
									Next Step
								</button>
							)}
							<button
								id="save-prompt-button"
								className="btn btn-alert mr-2"
								onClick={handleSave}
								disabled={disableSave}>
								Save Prompt
							</button>
						</div>
					</div>
				</section>
			)}

			{view == "raw" && (
				<>
					<RtkqResultViewer
						heading="Default Prompt Info"
						result={promptId ? getEditPromptDefaultsResult : getNewPromptDefaultsResult}
					/>
					<RtkqResultViewer
						heading="Available Teachers List"
						result={getEmailListResult}
					/>
				</>
			)}
		</>
	);
}
